import { create } from 'zustand';
import { Organization } from '../../../types/Organization';
import ResponseError from '../../../types/response/ResponseError';

export interface OrganizationsStore {
    organizations: Organization[];
    error: ResponseError;
    isEnablingOrganization: boolean;
    organizationEnabled: boolean;
    enableOrganizationError: ResponseError | undefined;
    isDeletingOrganization: boolean;
    isDeletedOrganization: boolean;
    deleteOrganizationError: ResponseError | undefined;
    deletedOrganization: string;
    isCreatingOrganization: boolean;
    isCreatedOrganization: boolean;
    createOrganizationError: ResponseError | undefined;
    addMerchantError: ResponseError | undefined;
    inviteAdministratorError: ResponseError | undefined;
    createdOrganizationName: string;
    getOrganizationsSucceeded: (organizations: Organization[]) => void;
    getOrganizationsFailed: (error: ResponseError) => void;
    enableOrganizationStarted: () => void;
    enableOrganizationSucceeded: () => void;
    enableOrganizationFailed: (error: ResponseError) => void;
    clearEnableOrganization: () => void;
    deleteOrganizationStarted: () => void;
    deleteOrganizationSucceeded: (organization: string) => void;
    deleteOrganizationFailed: (error: ResponseError) => void;
    clearDeleteOrganization: () => void;
    createOrganizationStarted: () => void;
    createOrganizationSucceeded: (organizationName: string) => void;
    createOrganizationFailed: (error: ResponseError) => void;
    addMerchantFailed: (error: ResponseError) => void;
    inviteAdministratorFailed: (error: ResponseError) => void;
    clearCreateOrganization: () => void;
    reset: () => void;
}

export const initialState = {
    organizations: [] as Organization[],
    error: {} as ResponseError,
    isEnablingOrganization: false,
    organizationEnabled: false,
    enableOrganizationError: undefined,
    isDeletingOrganization: false,
    isDeletedOrganization: false,
    deleteOrganizationError: undefined,
    deletedOrganization: '',
    isCreatingOrganization: false,
    isCreatedOrganization: false,
    createOrganizationError: undefined,
    addMerchantError: undefined,
    inviteAdministratorError: undefined,
    createdOrganizationName: '',
};

export const useOrganizationsStore = create<OrganizationsStore>(set => ({
    ...initialState,

    getOrganizationsSucceeded: (organizations: Organization[]) =>
        set({
            organizations: organizations,
        }),

    getOrganizationsFailed: (error: ResponseError) =>
        set({
            error: error,
        }),

    enableOrganizationStarted: () =>
        set({
            isEnablingOrganization: true,
        }),

    enableOrganizationSucceeded: () =>
        set({
            organizationEnabled: true,
            isEnablingOrganization: false,
        }),

    enableOrganizationFailed: (error: ResponseError) =>
        set({
            enableOrganizationError: error,
            isEnablingOrganization: false,
        }),

    clearEnableOrganization: () =>
        set({
            organizationEnabled: false,
            isEnablingOrganization: false,
            enableOrganizationError: undefined,
        }),

    deleteOrganizationStarted: () =>
        set({
            isDeletingOrganization: true,
        }),

    deleteOrganizationSucceeded: (organization: string) =>
        set({
            isDeletingOrganization: false,
            isDeletedOrganization: true,
            deletedOrganization: organization,
        }),

    deleteOrganizationFailed: (error: ResponseError) =>
        set({
            deleteOrganizationError: error,
            isDeletingOrganization: false,
        }),

    clearDeleteOrganization: () =>
        set({
            isDeletingOrganization: false,
            isDeletedOrganization: false,
            deleteOrganizationError: undefined,
            deletedOrganization: '',
        }),

    createOrganizationStarted: () =>
        set({
            isCreatingOrganization: true,
            createOrganizationError: undefined,
            addMerchantError: undefined,
            inviteAdministratorError: undefined,
        }),

    createOrganizationSucceeded: (organizationName: string) =>
        set({
            isCreatingOrganization: false,
            isCreatedOrganization: true,
            createdOrganizationName: organizationName,
        }),

    createOrganizationFailed: (error: ResponseError) =>
        set({
            createOrganizationError: error,
            isCreatingOrganization: false,
        }),

    addMerchantFailed: (error: ResponseError) =>
        set({
            addMerchantError: error,
            isCreatingOrganization: false,
        }),

    inviteAdministratorFailed: (error: ResponseError) =>
        set({
            inviteAdministratorError: error,
            isCreatingOrganization: false,
        }),

    clearCreateOrganization: () =>
        set({
            isCreatingOrganization: false,
            isCreatedOrganization: false,
            createOrganizationError: undefined,
            addMerchantError: undefined,
            inviteAdministratorError: undefined,
            createdOrganizationName: '',
        }),

    reset: () => set(initialState),
}));
